// English strings for Plans & pricing and the vendor control centre (BILLING-001 / SYSTEM-001, owner
// 2026-10-25; D-065). Spread into `en` as `en.plans`, so keys read `t("plans.…")`. Feature labels are
// keyed by the catalog key in @ady/contracts plan-features.ts. Copy rules: DESIGN_SYSTEM §9.6.
export const plansStrings = {
  /** The Free plan's name where no plan is recorded (Settings → Billing). */
  free: "Free plan",
  groups: {
    selling: "Selling and orders",
    channels: "Sales channels",
    catalog: "Products and stock",
    customers: "Customers",
    marketing: "Marketing",
    money: "Money",
    store: "Online store and reports",
    ai: "root AI",
    team: "Team",
    developers: "Apps and developers",
    services: "Adyvance Services",
    limits: "Limits",
    credits: "Monthly credits",
  },
  features: {
    order_management: "Orders",
    returns: "Returns and exchanges",
    delivery: "Delivery and couriers",
    checkout_settings: "Checkout settings",
    product_catalog: "Products, categories and brands",
    inventory: "Stock",
    bulk_import: "Import products and move store",
    media_library: "Files and media",
    customer_list: "Customers",
    payments: "Payments",
    money_overview: "Money overview and COD",
    taxes: "Taxes",
    marketing_automations: "Marketing automations",
    store_notifications: "Store emails and SMS",
    online_store: "Online store and theme",
    seo_tools: "SEO",
    store_policies: "Store policies",
    reports: "Reports",
    integrations: "Integrations",
    pos: "Point of sale (POS)",
    pos_offline: "POS works offline",
    cod_checks: "COD confirmation calls",
    multi_courier_routing: "Smart courier routing",
    abandoned_checkouts: "Unfinished checkouts",
    installation: "Installation services",
    conversations: "Sampark inbox",
    marketplace_sync: "Daraz and marketplaces",
    customer_accounts: "Customer accounts on the store",
    advanced_inventory: "Stock transfers and batches",
    digital_products: "Digital products",
    bundles: "Product bundles",
    segments: "Customer segments",
    reviews: "Reviews",
    loyalty: "Loyalty points",
    promotions: "Discounts and coupons",
    flash_sales: "Flash deals",
    offer_popup: "Offer pop-up",
    newsletter: "Newsletter",
    payment_links: "Payment links",
    scheduled_campaigns: "SMS and email campaigns",
    affiliates: "Affiliates",
    dedicated_sender_id: "Own SMS sender name",
    bulk_sms: "Bulk SMS",
    finance_advanced: "Accounts and statements",
    expenses: "Expenses and suppliers",
    custom_domain: "Own domain",
    page_editor: "Page editor",
    custom_code: "Code editor",
    blog: "Blog",
    remove_attribution: "Remove “Powered by Adyvance”",
    traffic_analytics: "Visitor reports",
    advanced_analytics: "Advanced reports",
    ai_assistant: "root assistant",
    shopping_assistant: "Shopping assistant on the store",
    mcp_access: "Connect AI tools (MCP)",
    custom_roles: "Custom staff roles",
    public_api: "API keys",
    webhooks: "Webhooks",
    headless_api: "Headless storefront API",
    import_marketplace: "Product Import",
    sms_service: "SMS Service",
    digital_marketing: "Digital Marketing",
    design_content: "Design and content",
    growth_strategy: "Growth strategy",
    courses: "Courses",
    stores: "Stores",
    products: "Products",
    orders_month: "Orders a month",
    staff: "Staff seats",
    locations: "Locations",
    pos_registers: "POS registers",
    storage_bytes: "File storage",
    automations: "Automations",
    api_keys: "API keys",
    webhook_endpoints: "Webhook endpoints",
    api_rate_per_min: "API requests a minute",
    theme_versions: "Saved theme versions",
    audit_retention_days: "Activity log kept (days)",
    test_mode_stores: "Test stores",
    sms_month: "SMS credits a month",
    ai_credits_month: "root AI credits a month",
  },
  units: {
    unlimited: "Unlimited",
    gb: "{n} GB",
    days: "{n} days",
    perMonth: "{n} a month",
    none: "None",
  },
  // ---------- owner console: Plans & pricing ----------
  console: {
    title: "Plans & pricing",
    lead: "Make the plans shops buy from their Adyvance Wallet: price, icon, every feature, limits and monthly credits.",
    newPlan: "New plan",
    newPlanTitle: "Make a plan",
    name: "Plan name",
    namePlaceholder: "Growth",
    copyFrom: "Start from",
    copyFromDefault: "The default plan",
    create: "Make plan",
    created: "Plan made. Set its price and features, then put it on sale.",
    saved: "Plan saved",
    compare: "Compare plans",
    cards: "Plans",
    shops: "{n} shops",
    shops_one: "1 shop",
    mrr: "{amount} a month",
    paying: "Paying shops",
    totalMrr: "Monthly revenue",
    allShops: "Shops on Adyvance",
    onSale: "On sale",
    draft: "Not on sale",
    // Owner, 2026-11-05: on sale or not from the card, and deleting a plan no shop has had.
    saleToggle: "Sell {plan}",
    nowOnSale: "{plan} is on sale",
    nowOffSale: "{plan} is off sale. Shops already on it keep it.",
    delete: "Delete plan",
    deleteNamed: "Delete {plan}",
    deleteTitle: "Delete {plan}?",
    deleteBody:
      "It goes from your plans for good. Only a plan no shop has ever had can be deleted; the others can be taken off sale.",
    deleted: "Plan deleted",
    errors: {
      priceRequired: "Give the plan a price first (Edit), then put it on sale.",
      inUse: "Shops have had this plan, so it stays for their history. Take it off sale instead.",
      coupon: "A coupon is for this plan. Change the coupon first.",
      notice: "A message to shops names this plan, so it can't be deleted.",
      default: "The default plan is always on and can't be deleted.",
    },
    default: "Default plan",
    defaultBody: "Every shop is on this plan until it buys one. It has no price.",
    edit: "Edit",
    perMonth: "/month",
    perYear: "/year",
    yearlySave: "Yearly saves {pct}%",
    yearlySame: "Yearly saves nothing",
    noPrice: "No price yet",
    editorTitle: "Edit {plan}",
    sections: {
      basics: "Plan",
      price: "Price",
      features: "Features",
      limits: "Limits",
      credits: "Monthly credits",
      preview: "How shops see it",
    },
    summary: "Short description",
    summaryPlaceholder: "For shops taking orders every day",
    badge: "Badge",
    badgePlaceholder: "Most popular",
    icon: "Icon",
    iconHelp: "Shops see this icon on every feature the plan unlocks.",
    monthly: "Monthly price",
    yearly: "Yearly price",
    yearlyHint: "12 months at the monthly price is {amount}.",
    yearlySuggest: "Use {amount} (2 months free)",
    trial: "Free trial days",
    trialHelp: "0 or empty: no trial.",
    sale: "On sale",
    saleHelp: "Shops can buy it from their wallet.",
    unlimited: "Unlimited",
    creditsHelp:
      "Given every month. Unused credits don't carry over; packs a shop buys never expire.",
    allOn: "All on",
    allOff: "All off",
    search: "Find a feature",
    on: "{n} of {total} on",
    losing: "{n} shops on this plan lose {feature} when you save.",
    losing_one: "1 shop on this plan loses {feature} when you save.",
    unsaved: "Unsaved changes",
    discard: "Discard",
    save: "Save plan",
    order: "Order",
    up: "Move up",
    down: "Move down",
    checksTitle: "Pricing checks",
    checksNone: "No problems found.",
    checks: {
      no_price: "{plan} has no price, so shops can't buy it.",
      yearly_not_cheaper: "{plan}: the yearly price saves nothing against 12 monthly payments.",
      yearly_discount_big: "{plan}: yearly is {pct}% off. Check that is what you want.",
      cheaper_has_more:
        "{plan} costs more than {cheaper} but has {n} features off that {cheaper} has on.",
      lower_limit_than_cheaper: "{plan} costs more than {cheaper} but has {n} lower limits.",
      same_as_other: "{plan} gives exactly the same as {other}.",
      losing_shops: "Shops lose features on save.",
    },
    preview: {
      choose: "Choose",
      current: "Current plan",
      includes: "Includes",
      more: "+ {n} more",
    },
  },
  // ---------- owner console: overview ----------
  overview: {
    title: "Overview",
    lead: "Everything running on Adyvance, at a glance.",
    greeting: {
      morning: "Good morning, {name}",
      afternoon: "Good afternoon, {name}",
      evening: "Good evening, {name}",
    },
    statsFor: "Here is Adyvance today,",
    subs: {
      shops: "Shops on Adyvance · new shops, last 30 days",
      active: "Shops that opened their admin in 7 days",
      mrr: "Paid plans, per month",
      earned: "Spent from wallets this month · last 6 months",
    },
    quick: {
      vendors: "Every shop, its plan, wallet and health.",
      plans: "Prices, features, limits and credits.",
      billing: "Top-ups to check and wallet money.",
      import: "Wholesale orders and payments.",
      support: "Tickets and help articles.",
    },
    series: { shops: "New shops", earned: "Earned" },
    shops: "Shops",
    activeWeek: "Active this week",
    newMonth: "New this month",
    paying: "Paying",
    mrr: "Monthly plan revenue",
    earned: "Earned this month",
    earnedVs: "{amount} last month",
    held: "Wallet money held",
    topupsWaiting: "{n} top-ups to check",
    plansTitle: "Shops by plan",
    renewalsTitle: "Renewals",
    renewSoon: "Renew in 7 days",
    renewOff: "Auto-renew off",
    renewShort: "Wallet short",
    inGrace: "In grace",
    attentionTitle: "Needs you",
    attentionNone: "Nothing is waiting.",
    attention: {
      topups: "Wallet top-ups to check",
      kyc: "Business verifications",
      import_payments: "Import payments to check",
      import_requests: "Import requests",
      sms: "SMS purchases to check",
      ads: "Ads to review",
      tickets: "Open support tickets",
      ideas: "New ideas from shops",
      reports: "New reports about shops",
      at_risk: "Shops at risk",
      jobs: "Failed jobs",
    },
    signupsTitle: "New shops, last 30 days",
    earnedTitle: "Earned, last 6 months",
    recentTitle: "Recent staff actions",
    recentNone: "No staff actions yet.",
    lifecycle: {
      trial: "Trial",
      active: "Active",
      past_due: "Past due",
      suspended: "Suspended",
      closed: "Closed",
    },
  },
  // ---------- owner console: vendors ----------
  vendors: {
    title: "Vendors",
    lead: "Every shop on Adyvance: plan, wallet, health, and what you can do for it.",
    tabs: { shops: "Shops", setup: "Setup", messages: "Messages", verification: "Verification" },
    search: "Search name, address or email",
    allStates: "Every state",
    allPlans: "Every plan",
    allFlags: "Everything",
    columns: {
      shop: "Shop",
      plan: "Plan",
      wallet: "Wallet",
      state: "State",
      health: "Health",
      team: "Team",
      lastActive: "Last active",
      joined: "Joined",
    },
    none: "No shops match.",
    count: "{n} shops",
    exportCsv: "Export CSV",
    flags: {
      renewal_soon: "Renews soon",
      renewal_short: "Renewal short",
      grace: "In grace",
      low_wallet: "Low wallet",
      topup_waiting: "Top-up waiting",
      kyc_waiting: "Verification waiting",
      ticket_open: "Open ticket",
      inactive: "Inactive 14+ days",
      access_requested: "Access asked",
      at_risk: "At risk",
      new: "New",
    },
    planGiven: "given",
    planUntil: "until {date}",
    never: "Never",
    back: "All shops",
    detail: {
      owner: "Owner",
      stores: "Stores",
      openStore: "Open store",
      actions: "Actions",
      suspend: "Suspend shop",
      reactivate: "Reactivate shop",
      changePlan: "Change plan",
      message: "Send message",
      viewAsShop: "View as shop",
      suspendedBanner: "Suspended on {date}: {reason}",
      tabs: {
        overview: "Overview",
        plan: "Plan and features",
        wallet: "Wallet",
        services: "Services",
        support: "Support",
        activity: "Activity",
      },
      usage: "Usage this month",
      usageOf: "{used} of {limit}",
      usageUnlimited: "{used} · unlimited",
      planHistory: "Plan history",
      noHistory: "Always on the default plan.",
      given: "Given by Adyvance",
      paid: "Paid {amount}",
      services: {
        // Bought packs left (the plan's monthly credits are under Usage this month).
        sms: "SMS credits bought, left",
        ai: "root AI credits bought, left",
        ads: "Ads running",
        imports: "Import orders open",
        themes: "Premium themes",
      },
      tickets: "Support tickets",
      noTickets: "No tickets.",
      activity: "Recent activity",
      noActivity: "Nothing recorded yet.",
      walletRecent: "Recent wallet activity",
      spent30: "Spent in 30 days",
      topupsWaiting: "Top-ups waiting",
      openWallet: "Open in Wallet and billing",
    },
    features: {
      title: "This shop's features",
      lead: "Turn a feature on or off, or change a limit, just for this shop. The plan's own value is shown beside it.",
      plan: "Plan: {value}",
      override: "For this shop",
      until: "Until",
      forever: "No end date",
      note: "Why (optional)",
      reset: "Use the plan's value",
      save: "Save for this shop",
      saved: "Saved for this shop",
      changed: "{n} changed",
    },
    lifecycle: {
      suspendTitle: "Suspend {shop}?",
      suspendBody:
        "The admin turns read-only and the storefront stops taking orders. Orders, payments and courier updates already under way keep working.",
      reactivateTitle: "Reactivate {shop}?",
      reactivateBody: "The admin and storefront work normally again.",
      reason: "Reason",
      reasonHelp: "The shop sees this on its read-only banner.",
      suspendOk: "Shop suspended",
      reactivateOk: "Shop reactivated",
      confirm: "Yes, {action}",
    },
    plan: {
      title: "Change {shop}'s plan",
      give: "Give a plan",
      giveBody: "No charge. The shop gets the plan for the days you set.",
      extend: "Add free days",
      extendBody: "Adds days to the plan the shop has now.",
      default: "Move to the default plan",
      defaultBody: "Ends the shop's current plan now. No money goes back.",
      plan: "Plan",
      days: "Days",
      presets: { d7: "7 days", d30: "1 month", d90: "3 months", d365: "1 year" },
      note: "Note (kept in the activity log)",
      ok: "Plan changed",
      current: "Now: {plan}",
    },
    access: {
      title: "View as shop",
      lead: "See this shop's admin exactly as the shop does, read-only. The shop must allow it first; it sees a banner the whole time and every page you open is logged.",
      reason: "Why do you need to look?",
      reasonPlaceholder: "Checking why the order page shows an error",
      minutes: "For how long",
      ask: "Ask the shop",
      asked: "Asked. The shop allows or denies it from its admin.",
      waiting: "Waiting for the shop to allow it",
      approved: "Allowed until {time}",
      open: "Open read-only view",
      end: "End access",
      ended: "Access ended",
      history: "Access history",
      views: "{n} pages viewed",
      states: {
        requested: "Asked",
        approved: "Allowed",
        denied: "Denied",
        revoked: "Ended by the shop",
        expired: "Time up",
        ended: "Ended",
      },
      durations: { m30: "30 minutes", m60: "1 hour", m120: "2 hours", m240: "4 hours" },
    },
  },
  // ---------- owner console: messages to shops ----------
  notices: {
    title: "Messages to shops",
    lead: "Tell every shop, the shops on one plan, or one shop. Messages show in the admin; a pinned one shows as a banner.",
    new: "New message",
    audience: "Send to",
    audiences: { all: "Every shop", plan: "Shops on a plan", org: "One shop" },
    plan: "Plan",
    shop: "Shop",
    tone: "Kind",
    tones: { info: "Info", success: "Good news", warning: "Heads-up", critical: "Urgent" },
    titleLabel: "Title",
    body: "Message",
    link: "Link (optional)",
    linkHelp: "A page in the admin like /wallet, or https://…",
    pin: "Show as a banner for",
    pinNone: "Don't pin",
    pinDays: "{n} days",
    pinDay: "1 day",
    email: "Also email the owners",
    emailSoon: "Soon: needs the sending identity",
    send: "Send message",
    sent: "Message sent",
    none: "No messages sent yet.",
    seen: "Seen by {n}",
    withdraw: "Withdraw",
    withdrawn: "Withdrawn",
    pinnedUntil: "Pinned until {date}",
    to: {
      all: "Every shop",
      plan: "Shops on {plan}",
      org: "{shop}",
    },
  },
  // ---------- vendor admin ----------
  shop: {
    lockedIn: "In {plan}",
    lockedNone: "Not in your plan",
    lockedTitle: "{feature} is part of the {plan} plan",
    lockedTitleNone: "{feature} isn't in your plan",
    lockedBody:
      "Upgrade to {plan} to use it. Your data is kept: you can still see it, and everything works again as soon as you upgrade.",
    lockedBodyNone: "Ask Adyvance about a plan that includes it. Your data is kept.",
    from: "From {amount}/month",
    upgrade: "Upgrade to {plan}",
    seePlans: "See plans",
    alsoIncludes: "{plan} also includes",
    notices: {
      title: "From Adyvance",
      close: "Close",
      open: "Open",
    },
    access: {
      askTitle: "Adyvance support asks to view your shop",
      askBody:
        "{staff} wants a read-only look for {minutes} minutes: “{reason}”. They can't change anything, and you can end it any time.",
      allow: "Allow",
      deny: "Deny",
      allowed: "Support can view your shop",
      denied: "Request denied",
      liveTitle: "Adyvance support is viewing your shop (read-only) until {time}",
      end: "End now",
      endedOk: "Support access ended",
      supportView: "Support view of {shop}: read-only until {time}",
      leave: "Leave support view",
    },
    suspended:
      "Your shop is suspended: {reason}. The admin is read-only and the store isn't taking orders. Contact Adyvance support.",
  },
} as const;
